/**
 * Materials needed — `purchasing-main.png`.
 *
 * What ops looks at first thing: what the next seven days of visits need,
 * what the yard has, and what has to be bought. Grouped by category because
 * that is how a yard is walked and how a vendor's catalogue is laid out.
 *
 * No costs anywhere on this screen, and the footer says so. Plan §6.7: cost
 * attaches when the crew closes out, not when ops plans, and a price here
 * would be a guess that looked like a fact.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { PageBar } from '../frame/page-bar.js'
import { StockBar } from '../components/stock-bar.js'
import { Button, Card, EmptyState, MetricCard, SectionLabel, Tag } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { formatNumber, parseDateOnly } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import {
  DEMAND_STATUS, type DemandResponse, type DemandRow, type DemandStatus,
} from '../lib/purchasing-types.js'

const TABS = [
  { key: 'materials', label: 'Materials needed' },
  { key: 'purchase-orders', label: 'Purchase orders' },
  { key: 'inventory', label: 'Inventory' },
] as const

const WINDOWS = [
  { days: 1, label: 'Today' },
  { days: 7, label: 'Next 7 days' },
  { days: 14, label: 'Next 14 days' },
] as const

export function Purchasing({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const days = Number(params.get('days') ?? 7)
  const filter = (params.get('status') as DemandStatus | null) ?? null
  const selectedItem = params.get('item')

  const [data, setData] = useState<DemandResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = me.permissions.purchasing === 'edit' || me.permissions.purchasing === 'full'

  const load = useCallback(async () => {
    try {
      setData(await request<DemandResponse>(`/purchasing/demand?days=${days}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Demand could not load.')
    }
  }, [days])

  useEffect(() => { void load() }, [load])

  const rows = useMemo(
    () => (data?.items ?? []).filter((row) => !filter || row.status === filter),
    [data, filter])

  const selected = useMemo(
    () => data?.items.find((row) => row.itemId === selectedItem) ?? null,
    [data, selectedItem])

  async function createOrders() {
    if (!data) return
    const needs = data.items
      .filter((row) => row.shortfall > 0)
      .map((row) => ({
        itemId: row.itemId,
        yardId: row.yardId,
        qty: row.shortfall,
        visitId: row.visits[0]?.id ?? null,
      }))

    if (needs.length === 0) {
      setNotice('Nothing is short — there is nothing to order.')
      return
    }

    setBusy(true)
    try {
      const result = await request<{ purchaseOrderIds: string[]; skipped: { why: string }[] }>(
        '/purchasing/purchase-orders/from-needs',
        { method: 'POST', body: { needs, expectedOn: data.window.to } })

      const made = result.purchaseOrderIds.length
      setNotice(
        made === 0
          ? `Nothing was ordered. ${result.skipped[0]?.why ?? ''}`.trim()
          : `${made} draft ${made === 1 ? 'order' : 'orders'} created, grouped by vendor.`
            + (result.skipped.length > 0
              ? ` ${result.skipped.length} item${result.skipped.length === 1 ? '' : 's'} skipped — no vendor set.`
              : ''))
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'Those orders could not be created.')
    } finally {
      setBusy(false)
    }
  }

  const grouped = useMemo(() => {
    const groups = new Map<string, DemandRow[]>()
    for (const row of rows) {
      const key = row.categoryName ?? 'Uncategorised'
      groups.set(key, [...(groups.get(key) ?? []), row])
    }
    return [...groups.entries()]
  }, [rows])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageBar
        title="Purchasing"
        sub={`${me.scope.label} · ${WINDOWS.find((w) => w.days === days)?.label.toLowerCase() ?? `next ${days} days`}`}
        tabs={TABS.map((tab) => ({ key: tab.key, label: tab.label }))}
        activeTab="materials"
        onTabChange={(key) => { if (key !== 'materials') navigate(`/purchasing/${key}`) }}
        actions={
          <>
            <Button onClick={() => navigate('/purchasing/inventory?count=1')}>
              Inventory count
            </Button>
            {canEdit && (
              <Button variant="primary" disabled={busy} onClick={() => void createOrders()}>
                Create POs from needs
              </Button>
            )}
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {error && (
          <EmptyState title="Demand could not load" body={error}
            action={{ label: 'Try again', onClick: () => void load() }} />
        )}

        {data && (
          <>
            {/* The metric cards are the filter — clicking one narrows the list. */}
            <div className="mb-3 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-5">
              {(['order', 'short', 'ordered', 'ready', 'covered'] as const).map((status) => (
                <MetricCard
                  key={status}
                  label={CARD_LABEL[status]}
                  value={String(data.totals[status])}
                  sub={CARD_SUB[status]}
                  dotColor={DOT[status]}
                  selected={filter === status}
                  onClick={() => setParams((next) => {
                    const updated = new URLSearchParams(next)
                    if (filter === status) updated.delete('status')
                    else updated.set('status', status)
                    return updated
                  })}
                />
              ))}
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              {WINDOWS.map((option) => (
                <button
                  key={option.days}
                  type="button"
                  onClick={() => setParams((next) => {
                    const updated = new URLSearchParams(next)
                    updated.set('days', String(option.days))
                    return updated
                  })}
                  className={cn('rounded-chip border px-3 py-1 text-cell',
                    days === option.days
                      ? 'border-accent-line bg-accent-soft font-semibold text-accent-dark'
                      : 'border-line bg-surface text-text hover:bg-hover')}
                >
                  Visits · {option.label.toLowerCase()}
                </button>
              ))}
              <span className="text-cell text-text-muted">
                {data.totals.total} {data.totals.total === 1 ? 'material' : 'materials'} across{' '}
                {countVisits(data.items)} visits · grouped by category
              </span>
            </div>

            <div className={cn('grid gap-4', selected && 'xl:grid-cols-[1fr_minmax(340px,420px)]')}>
              <Card className="min-w-0 overflow-x-auto p-0">
                {rows.length === 0
                  ? (
                    <p className="m-0 p-6 text-body text-text-muted">
                      {filter
                        ? 'Nothing in that state right now.'
                        : 'No visits in this window need materials yet.'}
                    </p>
                  )
                  : (
                    <table className="w-full border-collapse text-body">
                      <thead>
                        <tr className="border-b border-line text-left">
                          {['Material', 'Needed', 'In stock', 'Reserved', 'To order',
                            'For visits', 'Vendor', 'Status'].map((head) => (
                            <th key={head}
                              className={cn('px-3 py-2 text-label text-text-muted',
                                ['Needed', 'In stock', 'Reserved', 'To order'].includes(head)
                                  && 'text-right')}>
                              {head.toUpperCase()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {grouped.map(([category, items]) => (
                          <GroupRows
                            key={category}
                            category={category}
                            items={items}
                            selectedItem={selectedItem}
                            onSelect={(row) => setParams((next) => {
                              const updated = new URLSearchParams(next)
                              updated.set('item', row.itemId)
                              return updated
                            })}
                          />
                        ))}
                      </tbody>
                    </table>
                  )}

                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-line p-3">
                  <span className="text-body font-bold text-text">
                    {data.totals.total} materials
                  </span>
                  <span className="text-cell text-accent-dark">
                    {data.totals.covered} covered from stock
                  </span>
                  <span className="text-cell text-warn">{data.totals.order} to order</span>
                  {data.totals.short > 0 && (
                    <span className="text-cell font-bold text-danger">
                      {data.totals.short} short — needs a swap
                    </span>
                  )}
                  {/* Plan §6.7's rule, said out loud where somebody might
                      otherwise go looking for a price. */}
                  <span className="ml-auto text-cell text-text-faint">
                    No costs shown here — costs attach when the crew closes out the job
                  </span>
                </div>
              </Card>

              {/* Swapping lives on the visit's material line now (`docs/visit-page.md` §9). */}
              {selected && (() => {
                const line = selected.visits.find((visit) => visit.materialLineId)
                return line ? (
                  <Card className="self-start p-4">
                    <Link
                      to={`/visits/${line.workItemId}?tab=materials&line=${line.materialLineId}`}
                      className="text-body font-semibold text-accent-dark hover:underline"
                    >
                      Swap on {line.workItemReference ?? line.reference}
                    </Link>
                  </Card>
                ) : null
              })()}
            </div>
          </>
        )}
      </div>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </div>
  )
}

function GroupRows({
  category, items, selectedItem, onSelect,
}: {
  category: string
  items: DemandRow[]
  selectedItem: string | null
  onSelect: (row: DemandRow) => void
}) {
  const dates = items.flatMap((row) => row.visits.map((visit) => visit.date)).sort()
  const span = dates.length > 0 && dates[0] !== dates[dates.length - 1]
    ? `${shortDate(dates[0]!)}–${shortDate(dates[dates.length - 1]!)}`
    : dates[0] ? shortDate(dates[0]) : ''

  return (
    <>
      <tr className="border-y border-line bg-surface-group">
        <td colSpan={8} className="px-3 py-1.5">
          <span className="flex flex-wrap items-baseline gap-2">
            <SectionLabel>{category}</SectionLabel>
            <span className="text-cell text-text-muted">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </span>
            {span && <span className="ml-auto text-cell text-text-faint">{span}</span>}
          </span>
        </td>
      </tr>

      {items.map((row) => {
        const badge = DEMAND_STATUS[row.status]
        return (
          <tr
            key={`${row.itemId}:${row.yardId}`}
            onClick={() => onSelect(row)}
            className={cn('cursor-pointer border-b border-line-soft',
              row.itemId === selectedItem ? 'bg-accent-soft' : 'hover:bg-hover')}
          >
            <td className="px-3 py-2.5">
              <span className="block text-body font-bold text-text">{row.itemName}</span>
              <span className="block text-cell text-text-muted">
                {[row.sku, row.unit].filter(Boolean).join(' · ')}
              </span>
            </td>
            <td className="px-3 py-2.5 text-right">
              <span className="text-body font-bold tnum text-text">
                {formatNumber(row.planned)}
              </span>
              <span className="text-cell text-text-muted"> {row.unit}</span>
            </td>
            <td className="px-3 py-2.5">
              <StockBar available={row.available} needed={row.planned} unit={row.unit} />
            </td>
            {/*
              * Reserved is how much of the need the yard can actually hold —
              * not the need again. Repeating `planned` here made every row
              * read as fully reserved, including the ones with nothing on the
              * shelf.
              */}
            <td className="px-3 py-2.5 text-right tnum text-text-muted">
              {formatNumber(Math.min(row.planned, Math.max(0, row.available)))}
            </td>
            <td className="px-3 py-2.5 text-right">
              {row.shortfall > 0
                ? <span className="font-bold tnum text-text">{formatNumber(row.shortfall)}</span>
                : <span className="text-text-faint">—</span>}
            </td>
            <td className="px-3 py-2.5">
              <span className="text-cell text-text-muted">
                {row.visits.slice(0, 3).map((visit) => visit.reference).join(' · ')}
                {row.visits.length > 3 && (
                  <span className="text-text-faint"> +{row.visits.length - 3}</span>
                )}
              </span>
            </td>
            <td className="px-3 py-2.5 text-cell text-text-muted">
              {row.vendorName ?? <span className="text-text-faint">—</span>}
            </td>
            <td className="px-3 py-2.5">
              <Tag tone={badge.tone} className="gap-1.5">
                <span aria-hidden
                  className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-chip', badge.dot)} />
                {badge.label}
              </Tag>
            </td>
          </tr>
        )
      })}
    </>
  )
}

const CARD_LABEL: Record<DemandStatus, string> = {
  order: 'To order',
  short: 'Short · needs a swap',
  ordered: 'Ordered · in transit',
  ready: 'Ready for pickup',
  covered: 'Covered from stock',
}

const CARD_SUB: Record<DemandStatus, string> = {
  order: 'no order raised yet',
  short: 'nothing arrives in time',
  ordered: 'a placed order covers it',
  ready: 'waiting on the vendor’s counter',
  covered: 'reserved to visits',
}

const DOT: Record<DemandStatus, string> = {
  order: 'rgb(var(--color-warn))',
  short: 'rgb(var(--color-danger))',
  ordered: 'rgb(var(--color-info))',
  ready: 'rgb(var(--color-accent))',
  covered: 'rgb(var(--color-accent))',
}

const countVisits = (rows: DemandRow[]): number =>
  new Set(rows.flatMap((row) => row.visits.map((visit) => visit.id))).size

const shortDate = (iso: string): string =>
  parseDateOnly(iso)?.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  }) ?? iso
